import { useLocation } from "preact-iso";
import { useEffect, useRef, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient } from "../../constants.ts";
import { Button, PageSpinner } from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import type { HandoffMessage } from "../../client/src/apiTyping.ts";

const formatTime = (ms: number): string => {
  const d = new Date(ms);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatRemainingTime = (expiresAt?: number): string => {
  if (!expiresAt) return "";
  const remainingMs = expiresAt - Date.now();
  if (remainingMs <= 0) return "Expired";
  const hours = Math.floor(remainingMs / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
};

export const HandoffPage = () => {
  const { url } = useLocation();
  const [messages, setMessages] = useState<HandoffMessage[]>([]);
  const [isHandoffActive, setIsHandoffActive] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number | undefined>();
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  usePageMeta({
    title: "Human Handoff | prompt2bot",
    description: "Take over conversations and reply to users directly.",
  });

  const parsedUrl = new URL(
    typeof location !== "undefined"
      ? location.href
      : "https://prompt2bot.com" + url,
  );
  const pathParts = parsedUrl.pathname.split("/handoff/")[1]?.split("/");
  const botId = pathParts?.[0];
  const conversationId = pathParts?.[1] ? decodeURIComponent(pathParts[1]) : "";
  const token = parsedUrl.searchParams.get("token") || undefined;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversation = async (silent = false) => {
    if (!botId || !conversationId) return;
    if (!silent) setIsLoading(true);
    try {
      const res = await apiClient({
        endpoint: "get-handoff-conversation",
        payload: {
          botId,
          targetConversationId: conversationId,
          secret: token,
        },
      });
      if (res.success) {
        setMessages(res.messages);
        setIsHandoffActive(res.isHandoffActive);
        setExpiresAt(res.expiresAt);
      }
    } catch (err) {
      console.error("Failed to load handoff conversation:", err);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConversation();
    const interval = setInterval(() => {
      fetchConversation(true);
    }, 4000);
    return () => clearInterval(interval);
  }, [botId, conversationId, token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  const handleSend = async () => {
    const trimmed = inputText.trim();
    if (!trimmed || isSending || !botId || !conversationId) return;

    setIsSending(true);
    try {
      const res = await apiClient({
        endpoint: "handoff-reply",
        payload: {
          botId,
          targetConversationId: conversationId,
          secret: token || "",
          text: trimmed,
        },
      });
      if (res.success) {
        setInputText("");
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            from: "You (Operator)",
            text: trimmed,
            time: Date.now(),
            isOwn: true,
          },
        ]);
        toast.success("Reply sent");
      } else {
        toast.error(res.error || "Failed to send reply");
      }
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send reply",
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleEndHandoff = async () => {
    if (!botId || !conversationId || isEnding) return;
    setIsEnding(true);
    try {
      const res = await apiClient({
        endpoint: "end-handoff",
        payload: {
          botId,
          targetConversationId: conversationId,
          secret: token,
        },
      });
      if (res.success) {
        setIsHandoffActive(false);
        toast.success("Handoff ended. Bot responses resumed.");
      } else {
        toast.error(res.error || "Failed to end handoff");
      }
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to end handoff",
      );
    } finally {
      setIsEnding(false);
    }
  };

  const isWhatsapp = conversationId.includes("@c.us") ||
    conversationId.includes("@g.us");
  const lastParticipantMessage = [...messages]
    .reverse()
    .find((m) => !m.isOwn);
  const isWhatsappWindowExpired = isWhatsapp &&
    lastParticipantMessage &&
    Date.now() - lastParticipantMessage.time > 24 * 60 * 60 * 1000;

  if (!botId || !conversationId) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Invalid Handoff URL</h1>
        <p className="text-text-secondary-light dark:text-text-secondary">
          Please check the link provided in your notification.
        </p>
      </div>
    );
  }

  if (isLoading) return <PageSpinner />;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] max-w-4xl mx-auto px-4 py-4">
      {/* Top Header */}
      <div className="flex items-center justify-between p-4 mb-3 rounded-xl bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-lg">{conversationId}</span>
            <span
              className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                isHandoffActive
                  ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
              }`}
            >
              {isHandoffActive ? "Handoff Active (Bot Muted)" : "Bot Active"}
            </span>
          </div>
          {isHandoffActive && expiresAt && (
            <p className="text-xs text-text-secondary-light dark:text-text-secondary mt-0.5">
              Auto-resumes in {formatRemainingTime(expiresAt)}
            </p>
          )}
        </div>
        {isHandoffActive && (
          <Button
            onClick={handleEndHandoff}
            disabled={isEnding}
            className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium"
          >
            {isEnding ? "Resuming..." : "Hand Back to Bot"}
          </Button>
        )}
      </div>

      {/* WhatsApp Window Warning */}
      {isWhatsappWindowExpired && (
        <div className="mb-3 p-3 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 rounded-lg">
          ⚠️ WhatsApp 24-hour customer service window is closed. The user must
          send a new message before free-form replies can be delivered.
        </div>
      )}

      {/* Message Timeline */}
      <div className="flex-1 overflow-y-auto p-4 mb-3 rounded-xl bg-surface-light dark:bg-surface border border-border-subtle-light dark:border-border-subtle space-y-3">
        {messages.length === 0
          ? (
            <div className="h-full flex items-center justify-center text-text-secondary-light dark:text-text-secondary text-sm">
              No messages in this conversation yet.
            </div>
          )
          : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`flex flex-col ${
                  m.isOwn ? "items-end" : "items-start"
                }`}
              >
                <span className="text-[11px] text-text-secondary-light dark:text-text-secondary mb-1 px-1">
                  {m.from} · {formatTime(m.time)}
                </span>
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.isOwn
                      ? "bg-accent-light text-white dark:bg-accent rounded-br-none"
                      : "bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle rounded-bl-none"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))
          )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input Bar */}
      <div className="p-3 rounded-xl bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle shadow-sm flex items-end gap-2">
        <textarea
          value={inputText}
          onInput={(e) => setInputText((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type a reply to send to the user (Enter to send, Shift+Enter for new line)..."
          rows={2}
          className="flex-1 bg-transparent border-0 resize-none outline-none text-sm p-1 placeholder:text-text-secondary-light dark:placeholder:text-text-secondary"
        />
        <Button
          onClick={handleSend}
          disabled={!inputText.trim() || isSending}
          className="px-4 py-2 bg-accent-light dark:bg-accent text-white font-medium text-sm"
        >
          {isSending ? "Sending..." : "Send"}
        </Button>
      </div>
    </div>
  );
};
